import {
  DepreciationMethod,
  LuxuryCarChargeMethod,
  QuotePayPeriod,
} from "./Quote";
import { State } from "./State";

type Period = QuotePayPeriod | "total";
export type PeriodicAmounts<T> = Record<Period, T>;
export type GstAmountValues = {
  exGst: number;
  incGst: number;
  gst: number;
};

export type CalculationOutput<TResult> = {
  id: string;
  name: string;
  formula?: string;
  result: TResult;
};

export type YearlyCalculation<TResult> = {
  yearIndex: number;
  startDate: string;
  endDate: string;
  daysHeld: number;
  calculation: CalculationOutput<TResult>;
};

export type DepreciationCalculationResult = {
  startingAdjustableValue: number;
  endAdjustableValue: CalculationOutput<number>;
  depreciation: CalculationOutput<number>;
  depreciationMethod: DepreciationMethod;
};

export type NonDeductibleYearCalculationResult = {
  rentalExGstLcc: CalculationOutput<number>;
  employeeContribution: CalculationOutput<number>;
  leaseBenefit: CalculationOutput<CalculationOutput<number>>;
  interest: CalculationOutput<number>;
  depreciation: CalculationOutput<number>;
  runningCosts: CalculationOutput<number>;
  totalDeductible: CalculationOutput<number>;
  nonDeductible: CalculationOutput<number>;
  nonDeductibleCharge: CalculationOutput<number>;
};

export type DisposalBalancingAdjustmentCalculations = {
  salePriceOnExit: number;
  endAdjustableValue: number;
  salePriceProportionToCarLimit: CalculationOutput<number>;
  balancingAdjustment: CalculationOutput<number>;
  disposalAllowance: CalculationOutput<number>;
};

type LuxuryLeaseComponentCalculations = {
  annualRentalsExGstLcc: CalculationOutput<number>;
  annualRunningCostsExFinance: CalculationOutput<number>;
  annualInterest: CalculationOutput<number>;
  depreciationSchedule: YearlyCalculation<DepreciationCalculationResult>[];
  totalDepreciation: number;
  nonDeductibleSchedule: YearlyCalculation<NonDeductibleYearCalculationResult>[];
  totalLeaseBenefit: number;
  totalInterest: number;
  totalDeductible: number;
  totalNonDeductible: PeriodicAmounts<number>;
  totalNonDeductibleCharge: PeriodicAmounts<number>;
  disposalBalancingAdjustment: DisposalBalancingAdjustmentCalculations;
  includeDisposal: boolean;
  includeNonDeductible: boolean;
};

export type LuxuryLeaseCalculations = {
  initialCost: CalculationOutput<number>;
  isLuxuryLease: CalculationOutput<boolean>;
  luxuryLeaseComponent: LuxuryLeaseComponentCalculations | null;
  luxuryCarCharge: CalculationOutput<PeriodicAmounts<number>>;
};

export type SalaryComparisonCalculations = {
  salaryExSuper: number;
  salaryExSuperIncomeTax: CalculationOutput<number>;
  netSalary: CalculationOutput<number>;
  preTaxSacrifice: CalculationOutput<number>;
  salaryExPreTaxSacrifice: CalculationOutput<number>;
  salaryExPreTaxSacrificeIncomeTax: CalculationOutput<number>;
  netSalaryWithPackaging: CalculationOutput<number>;
  netSalaryNoPackaging: CalculationOutput<number>;
  grossCost: CalculationOutput<PeriodicAmounts<number>>;
  netCost: CalculationOutput<PeriodicAmounts<number>>;
  savings: CalculationOutput<PeriodicAmounts<number>>;
  runningCosts: CalculationOutput<PeriodicAmounts<number>>;
  savingsPercentage: CalculationOutput<number>;
};

export type QuoteCalculations = {
  inputs: {
    onroadVehiclePrice: number;
    carLimit: GstAmountValues;
    termMths: number;
    termYrs: number;
    period: QuotePayPeriod;
    periodsPerYear: number;
    gst: PeriodicAmounts<number>;
    lcc: PeriodicAmounts<number>;
    rentalsIncGstLcc: PeriodicAmounts<number>;
    finance: PeriodicAmounts<GstAmountValues>;
    residual: GstAmountValues;
    preTaxRunningCostDeductions: PeriodicAmounts<number>;
    employeeContribution: PeriodicAmounts<number>;
    startDate: string;
    financialYearStartDate: string;
    depreciationMethod: DepreciationMethod;
    luxuryCarChargeMethod: LuxuryCarChargeMethod;
    companyTaxRate: number;
    inputTaxedAmount: PeriodicAmounts<number>;
    inputTaxCredit: PeriodicAmounts<number>;
    salaryExSuper: number;
  };
  luxuryLease: LuxuryLeaseCalculations;
  salaryComparison: SalaryComparisonCalculations;
};

export type CarLoanComparisonCalculations = {
  balloonPayment: CalculationOutput<number>;
  loanRepayment: CalculationOutput<number>;
  totalPayments: CalculationOutput<number>;
  lifetimeCost: CalculationOutput<PeriodicAmounts<number>>;
  interest: CalculationOutput<number>;
};

export type CashComparisonCalculations = {
  netCostOfPurchase: CalculationOutput<number>;
  interestForgone: CalculationOutput<number>;
  lifetimeCost: CalculationOutput<PeriodicAmounts<number>>;
};

export type HomeLoanComparisonCalculations = {
  loanRepayment: CalculationOutput<number>;
  totalPayments: CalculationOutput<number>;
  loanPayout: CalculationOutput<number>;
  lifetimeCost: CalculationOutput<PeriodicAmounts<number>>;
  interest: CalculationOutput<number>;
};

export type FinanceComparisonCalculations = {
  inputs: {
    termMths: number;
    termYrs: number;
    onroadVehiclePrice: number;
    residual: GstAmountValues;
    novatedInterestRate: number;
    cashInterestRate: number;
    homeLoanInterestRate: number;
    homeLoanTermMths: number;
    carLoanInterestRate: number;
    carLoanTermYrs: number;
    runningCosts: PeriodicAmounts<number>;
    netCost: PeriodicAmounts<number>;
  };
  cash: CashComparisonCalculations;
  homeloan: HomeLoanComparisonCalculations;
  carloan: CarLoanComparisonCalculations;
};

export type SellPriceProRiskLPICalculationInput = {
  onroadVehiclePrice: number;
  basePremiumMultiplier: number;
  state: State;
  stampDutyRate: number;
  brokerFee: GstAmountValues;
};

export type SellPriceProRiskLPIAccidentCalculationInput = {
  onroadVehiclePrice: number;
  basePremiumMultiplier: number;
  state: State;
  stampDutyRate: number;
  brokerFee: GstAmountValues;
};

export type SellPriceCalculationInput =
  | SellPriceProRiskLPICalculationInput
  | SellPriceProRiskLPIAccidentCalculationInput;

export enum InclusionCalculationId {
  SellPriceProRiskLPI = "SellPriceProRiskLPI",
  SellPriceProRiskLPIAccident = "SellPriceProRiskLPIAccident",
}

export type QuoteInclusionCalculations<T> = {
  inputs: T;
  calculationId: InclusionCalculationId;
  basePremium: CalculationOutput<GstAmountValues> | null;
  stampDuty: CalculationOutput<number> | null;
  sellPrice: CalculationOutput<GstAmountValues>;
  revenue: CalculationOutput<GstAmountValues>;
  buyPrice: CalculationOutput<GstAmountValues>;
};

export type QuoteInclusonCalculationsMap = Record<
  string,
  {
    option: {
      id: string;
      name: string;
      description: string | null;
      available: boolean;
    };
    calculation: QuoteInclusionCalculations<SellPriceCalculationInput> | null;
  }
>;
